import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AFFILIATE, PRICE_SEN } from "@jomcerdik/shared";
import { CardGrid, FeatureCard, PageHeader } from "../components/FeatureCard";
import { Alert, Button, Field } from "../components/Form";
import { useMe } from "../hooks/useMe";
import { api, errorMessage } from "../lib/api";
import { formatMYR } from "../lib/format";
import { locale, t, tk } from "../lib/i18n";

type Dash = {
  eligible: boolean;
  perSaleSen: number | null;
  minPayoutSen?: number;
  holdDays?: number;
  affiliate: { code: string; status: string; link: string } | null;
  totals?: { visitors: number; sales: number; topups: number; pendingSen: number; earnedSen: number; balanceSen: number; paidSen: number };
  bank?: { bank: string; holder: string; account: string; masked: string } | null;
  commissions?: { amountSen: number; status: string; createdAt: string; approvableAt: string; kind: string; buyer: string }[];
  payouts?: { id: string; amountSen: number; status: string; requestedAt: string; paidAt: string | null; reference: string | null; reason: string | null; hasReceipt: boolean; to: string | null }[];
};

const d = (s: string | null) => (s ? new Date(s).toLocaleDateString(locale(), { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium" }) : "—");
const C_STATUS: Record<string, string> = { pending: tk("Tertahan"), approved: tk("Sedia dikeluarkan"), paid_out: tk("Sudah dibayar"), reversed: tk("Dibatalkan (refund)") };
const P_STATUS: Record<string, string> = { requested: tk("Diminta"), approved: tk("Diluluskan"), paid: tk("Dibayar"), rejected: tk("Ditolak") };
const ERR: Record<string, string> = {
  affiliate_not_eligible: tk("Program affiliate terbuka kepada 200 pembeli awal (RM69 / RM79) yang lesennya aktif."),
  payout_below_minimum: tk("Baki belum cukup minimum pengeluaran."),
  payout_already_requested: tk("Anda sudah ada permintaan bayaran yang belum selesai."),
  bank_account_required: tk("Simpan akaun bank anda dahulu."),
};
const msg = (e: unknown) => {
  const code = (e as { code?: string })?.code ?? "";
  return ERR[code] ? t(ERR[code]!) : errorMessage(e);
};

export function AffiliatePage() {
  const { me, loading } = useMe();
  return (
    <>
      <PageHeader art="cover/affiliate" title="JomCerdik Affiliate" subtitle={t("Kongsi link anda. Dapat komisen setiap jualan lesen dan setiap kali pelanggan anda tambah kredit.")} />
      <CardGrid>
        <FeatureCard title={t("Jualan lesen")} lines={[
          t("Pelanggan bayar {price}", { price: formatMYR(PRICE_SEN.STANDARD) }),
          t("Anda dapat {a} (pembeli RM69) / {b} (pembeli RM79)", { a: formatMYR(PRICE_SEN.STANDARD - PRICE_SEN.EARLY_BIRD_1), b: formatMYR(PRICE_SEN.STANDARD - PRICE_SEN.DIRECT) }),
        ]} />
        <FeatureCard title={t("Tambah kredit")} lines={[t("Pelanggan anda topup RM50"), t("Anda dapat RM10 setiap kali")]} />
        <FeatureCard title={t("Bayaran")} lines={[t("Tertahan {n} hari (tempoh refund)", { n: AFFILIATE.HOLD_DAYS }), t("Minimum keluar {min} · pindahan bank", { min: formatMYR(AFFILIATE.MIN_PAYOUT_SEN) }), t("Pengeluaran diproses setiap {n} hari (2 minggu sekali)", { n: AFFILIATE.PAYOUT_CYCLE_DAYS })]} />
      </CardGrid>
      <div className="mt-8">
        {loading ? <p className="text-sm text-muted">{t("Memuatkan…")}</p>
          : !me ? <Alert kind="info"><Link to="/login?next=/affiliate" className="underline">{t("Log masuk")}</Link> {t("untuk melihat link affiliate anda.")}</Alert>
          : <Dashboard />}
      </div>
    </>
  );
}

function Dashboard() {
  const [dash, setDash] = useState<Dash | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api<Dash>("/api/affiliate/me").then(setDash).catch((e) => setError(msg(e))); }, []);
  useEffect(load, [load]);

  async function join() {
    setBusy(true); setError(null);
    try { await api("/api/affiliate/join", { method: "POST", json: {} }); load(); } catch (e) { setError(msg(e)); } finally { setBusy(false); }
  }

  if (error && !dash) return <Alert kind="error">{error}</Alert>;
  if (!dash) return <p className="text-sm text-muted">{t("Memuatkan…")}</p>;
  if (!dash.affiliate) {
    return dash.eligible ? (
      <section className="rounded-card border border-surface-line p-5">
        <h2 className="text-lg font-semibold">{t("Anda layak jadi affiliate")}</h2>
        <p className="mt-1 text-sm text-muted">{t("Komisen anda: {amount} setiap jualan lesen + RM10 setiap topup pelanggan.", { amount: formatMYR(dash.perSaleSen ?? 0) })}</p>
        {error && <div className="mt-3"><Alert kind="error">{error}</Alert></div>}
        <Button className="mt-4" onClick={join} busy={busy}>{t("Dapatkan link affiliate saya")}</Button>
      </section>
    ) : (
      <Alert kind="info">{t(ERR.affiliate_not_eligible!)} {t("Program akan dibuka kepada pengguna lain melalui promosi akan datang.")}</Alert>
    );
  }
  const tot = dash.totals!;
  return (
    <div className="space-y-6">
      <LinkBox link={dash.affiliate.link} code={dash.affiliate.code} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t("Pelawat link")} value={String(tot.visitors)} />
        <Stat label={t("Jualan lesen")} value={String(tot.sales)} />
        <Stat label={t("Topup pelanggan")} value={String(tot.topups)} />
        <Stat label={t("Sudah dibayar")} value={formatMYR(tot.paidSen)} />
        <Stat label={t("Tertahan")} value={formatMYR(tot.pendingSen)} hint={t("{n} hari selepas jualan", { n: dash.holdDays ?? 14 })} />
        <Stat label={t("Baki boleh dikeluarkan")} value={formatMYR(tot.balanceSen)} strong />
      </div>
      <Payout dash={dash} onDone={load} />
      <section>
        <h2 className="text-lg font-semibold">{t("Komisen terkini")}</h2>
        {dash.commissions!.length === 0 ? <p className="mt-1 text-sm text-muted">{t("Belum ada jualan. Kongsi link anda di WhatsApp, Telegram atau Facebook.")}</p> : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-muted"><tr><th className="py-1 pr-3">{t("Tarikh")}</th><th className="pr-3">{t("Jenis")}</th><th className="pr-3">{t("Pelanggan")}</th><th className="pr-3">{t("Jumlah")}</th><th>{t("Status")}</th></tr></thead>
              <tbody>
                {dash.commissions!.map((c, i) => (
                  <tr key={i} className="border-t border-surface-line">
                    <td className="py-1.5 pr-3">{d(c.createdAt)}</td>
                    <td className="pr-3">{c.kind === "topup" ? t("Topup kredit") : t("Lesen")}</td>
                    <td className="pr-3">{c.buyer}</td>
                    <td className="pr-3 tabular-nums">{formatMYR(c.amountSen)}</td>
                    <td>{C_STATUS[c.status] ? t(C_STATUS[c.status]!) : c.status}{c.status === "pending" ? ` ${t("(sehingga {date})", { date: d(c.approvableAt) })}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function LinkBox({ link, code }: { link: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const share = t("Jom guna JomCerdik — nota, kuiz dan kertas latihan ikut DSKP untuk anak-anak. Daftar di sini: {link}", { link });
  const copy = async (s: string) => { try { await navigator.clipboard.writeText(s); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ } };
  return (
    <section className="rounded-card border border-brand p-5">
      <h2 className="text-lg font-semibold">{t("Link affiliate anda")}</h2>
      <p className="mt-2 break-all rounded-lg bg-surface-muted p-3 font-mono text-sm">{link}</p>
      <p className="mt-1 text-xs text-muted">{t("Kod")}: <span className="font-mono">{code}</span></p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => copy(link)}>{copied ? t("Disalin ✓") : t("Salin link")}</Button>
        <a className="inline-flex items-center rounded-lg border border-surface-line px-4 py-2 text-sm" href={`https://wa.me/?text=${encodeURIComponent(share)}`} target="_blank" rel="noreferrer">{t("Kongsi di WhatsApp")}</a>
      </div>
    </section>
  );
}

function Stat({ label, value, hint, strong }: { label: string; value: string; hint?: string; strong?: boolean }) {
  return (
    <div className={`rounded-card border p-3 ${strong ? "border-brand" : "border-surface-line"}`}>
      <p className="text-xs text-muted">{label}</p>
      <p className={`mt-1 tabular-nums ${strong ? "text-xl font-bold" : "text-lg font-semibold"}`}>{value}</p>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

function Payout({ dash, onDone }: { dash: Dash; onDone: () => void }) {
  const [busy, setBusy] = useState<"bank" | "withdraw" | null>(null);
  const [editing, setEditing] = useState(!dash.bank);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const open = dash.payouts!.find((p) => p.status === "requested" || p.status === "approved");
  const balance = dash.totals!.balanceSen;
  const min = dash.minPayoutSen ?? AFFILIATE.MIN_PAYOUT_SEN;
  const bank = dash.bank;

  async function saveBank(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy("bank"); setError(null); setNotice(null);
    try {
      await api("/api/affiliate/bank", { method: "PUT", json: { bank: f.get("bank"), holder: f.get("holder"), account: f.get("account") } });
      setNotice(t("Akaun bank disimpan.")); setEditing(false); onDone();
    } catch (err) { setError(msg(err)); } finally { setBusy(null); }
  }
  async function withdraw() {
    if (!bank || !window.confirm(t("Keluarkan {amount} ke {bank} {acc} ({holder})?", { amount: formatMYR(balance), bank: bank.bank, acc: bank.masked, holder: bank.holder }))) return;
    setBusy("withdraw"); setError(null); setNotice(null);
    try {
      await api("/api/affiliate/payouts", { method: "POST", json: {} });
      setNotice(t("Permintaan {amount} dihantar. Anda akan terima emel bersama resit sebaik sahaja duit dipindahkan.", { amount: formatMYR(balance) }));
      onDone();
    } catch (err) { setError(msg(err)); } finally { setBusy(null); }
  }
  async function receipt(id: string) {
    try {
      const res = await fetch(`/api/affiliate/payouts/${id}/receipt`);
      if (!res.ok) throw new Error();
      // Download instead of window.open: mobile browsers block pop-ups opened after an await.
      const blob = await res.blob();
      const ext = blob.type === "application/pdf" ? "pdf" : blob.type.split("/")[1] ?? "jpg";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `resit-jomcerdik-${id.slice(0, 8)}.${ext}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } catch { setError(t("Resit tidak dapat dibuka. Cuba lagi.")); }
  }

  return (
    <section className="space-y-4 rounded-card border border-surface-line p-5">
      <h2 className="text-lg font-semibold">{t("Keluarkan duit")}</h2>
      {notice && <Alert kind="success">{notice}</Alert>}
      {error && <Alert kind="error">{error}</Alert>}

      <div>
        <h3 className="text-sm font-semibold">{t("Akaun bank untuk bayaran")}</h3>
        {bank && !editing ? (
          <p className="mt-1 text-sm">
            {bank.bank} · {bank.holder} · <span className="font-mono">{bank.masked}</span>{" "}
            <button type="button" className="ml-2 text-brand underline" onClick={() => setEditing(true)}>{t("Tukar")}</button>
          </p>
        ) : (
          <form onSubmit={saveBank} className="mt-2 grid gap-3 sm:grid-cols-3">
            <Field label={t("Bank / e-wallet")} name="bank" required defaultValue={bank?.bank} placeholder="Maybank" />
            <Field label={t("Nama pemegang akaun")} name="holder" required defaultValue={bank?.holder} />
            <Field label={t("No. akaun")} name="account" required inputMode="numeric" defaultValue={bank?.account} />
            <div className="flex gap-2 sm:col-span-3">
              <Button type="submit" busy={busy === "bank"}>{t("Simpan akaun bank")}</Button>
              {bank && <Button type="button" variant="secondary" onClick={() => setEditing(false)}>{t("Batal")}</Button>}
            </div>
          </form>
        )}
      </div>

      <div className="border-t border-surface-line pt-4">
        {(() => {
          const pending = dash.totals!.pendingSen;
          const reason = open
            ? t("Pengeluaran {amount} (diminta {date}) sedang diproses. Resit akan diemel kepada anda.", { amount: formatMYR(open.amountSen), date: d(open.requestedAt) })
            : !bank || editing
              ? t("Simpan akaun bank anda di atas dahulu.")
              : balance < min
                ? t("Baki boleh dikeluarkan {amount}. Minimum pengeluaran {min}.", { amount: formatMYR(balance), min: formatMYR(min) })
                : null;
          return (
            <>
              <Button onClick={withdraw} busy={busy === "withdraw"} disabled={reason !== null}>
                {t("Keluarkan {amount}", { amount: formatMYR(balance) })}
              </Button>
              {reason && <p className="mt-2 text-sm text-muted">{reason}</p>}
              <p className="mt-1 text-xs text-muted">{t("Pengeluaran dibayar setiap 2 minggu sekali. Permohonan dibayar pada pusingan seterusnya.")}</p>
              {pending > 0 && (
                <p className="mt-1 text-xs text-muted">
                  {t("{amount} lagi masih tertahan ({n} hari selepas jualan) dan akan masuk ke baki secara automatik.", { amount: formatMYR(pending), n: dash.holdDays ?? 14 })}
                </p>
              )}
            </>
          );
        })()}
      </div>

      {dash.payouts!.length > 0 && (
        <ul className="space-y-1 border-t border-surface-line pt-4 text-sm">
          {dash.payouts!.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-x-2">
              <span>{d(p.requestedAt)} · {formatMYR(p.amountSen)}{p.to ? ` → ${p.to}` : ""} · <strong>{P_STATUS[p.status] ? t(P_STATUS[p.status]!) : p.status}</strong></span>
              {p.reference && <span className="text-muted">{t("rujukan {ref}", { ref: p.reference })}</span>}
              {p.reason && <span className="text-muted">{p.reason}</span>}
              {p.hasReceipt && <button type="button" className="text-brand underline" onClick={() => receipt(p.id)}>{t("Lihat resit")}</button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
